import { useState, useRef, useCallback } from 'react';

/**
 * @param {*} initialValue 
 * @param {number} time
 * @returns {[
 *   any,
 *   any,
 *   (state: any) => void,
 *   (state: any) => void,
 * ]}
 */
const useDebouncedState = (initialValue, time = 300) => {
  const timeout = useRef();
  const [ state, setState ] = useState({
    realtime: initialValue,
    debounced: initialValue,
  });

  /**
   * Sets the state of the realtime value with the debounced value to update
   * after the time delay
   */
  const setDebouncedState = useCallback((stateOrFn) => {
    clearTimeout(timeout.current);
    setState(ps => {
      const newState = typeof stateOrFn === 'function'
        ? stateOrFn(ps.realtime)
        : stateOrFn;

      timeout.current = setTimeout(() => {
        setState(ps => ({...ps, debounced: newState}));
      }, time);
      
      return {
        ...ps,
        realtime: newState,
      };
    });
  }, [timeout, setState, time]);

  /**
   * Sets the realtime and debounced state immediately and cancels delayed updates
   * if there are any
   */
  const setImmediateState = useCallback((stateOrFn) => {
    clearTimeout(time.current);
    const newState = typeof stateOrFn === 'function'
      ? stateOrFn(ps.realtime)
      : stateOrFn;
    
    setState({realtime: newState, debounced: newState});
  }, [timeout, setState]);

  return [
    state.realtime,
    state.debounced,
    setDebouncedState,
    setImmediateState,
  ];
}

export default useDebouncedState;